import { useState } from "react"
import { ArrowRight } from "@untitledui/icons"
import type { BusinessDetails, ShopType } from "@/lib/api/onboarding-activation"
import {
  PrimaryButton,
  StageHeader,
  Field,
  AInput,
  ActionRow,
  ASelect,
  SelectTile,
  focusFirstInvalid,
  EMAIL_RE,
} from "./ui"

const COUNTRIES = ["United States", "Canada"]

// Step 1 — mirrors step 1 of CCi's Zoho onboarding form plus eTX's shop
// contact needs. Every field gives live positive feedback (emerald check)
// the moment it's validly filled; Continue validates on attempt and focuses
// the first miss.
export function ShopStep({
  draft,
  onBack,
  onNext,
}: {
  draft: BusinessDetails
  onBack: () => void
  onNext: (patch: BusinessDetails) => void
}) {
  const [shopType, setShopType] = useState<ShopType>(draft.shopType ?? "single")
  const [name, setName] = useState(draft.legalBusinessName ?? "")
  const [street, setStreet] = useState(draft.street ?? "")
  const [city, setCity] = useState(draft.city ?? "")
  const [state, setState] = useState(draft.state ?? "")
  const [zip, setZip] = useState(draft.zipCode ?? "")
  const [country, setCountry] = useState(draft.country ?? "")
  const [phone, setPhone] = useState(draft.shopPhone ?? "")
  const [email, setEmail] = useState(draft.shopEmail ?? "")
  const [tried, setTried] = useState(false)

  const errors = {
    name: !name.trim() && (shopType === "mso" ? "Enter your MSO's name" : "Enter your shop's name"),
    street: !street.trim() && "Enter the street address",
    city: !city.trim() && "Enter the city",
    state: !state.trim() && "Enter the state or province",
    zip: !zip.trim() && "Enter the postal / ZIP code",
    country: !country && "Pick a country",
    phone: !phone.trim() && "Enter the shop's phone number",
    email: !email.trim()
      ? "Enter the shop's email"
      : !EMAIL_RE.test(email.trim()) && "That email doesn't look right",
  }
  const missing = Object.values(errors).filter(Boolean).length

  const next = () => {
    if (missing > 0) {
      setTried(true)
      focusFirstInvalid()
      return
    }
    onNext({
      shopType,
      legalBusinessName: name.trim(),
      street: street.trim(),
      city: city.trim(),
      state: state.trim(),
      zipCode: zip.trim(),
      country,
      shopPhone: phone.trim(),
      shopEmail: email.trim(),
    })
  }

  const bad = (k: keyof typeof errors) => Boolean(tried && errors[k])

  return (
    <div>
      <StageHeader
        stage="Step 1 of 5"
        title="Your shop's details"
        lede="Type your shop's name, address, and contact details in the boxes below."
      />

      <div className="space-y-4">
        <Field label="Is this one shop or a group of shops?">
          <div className="grid grid-cols-1 gap-2.5 sm:grid-cols-2" role="radiogroup">
            <SelectTile selected={shopType === "single"} onClick={() => setShopType("single")}>
              One shop
            </SelectTile>
            <SelectTile selected={shopType === "mso"} onClick={() => setShopType("mso")}>
              A group of shops (MSO)
            </SelectTile>
          </div>
        </Field>

        <Field label={shopType === "mso" ? "Group (MSO) name" : "Shop name"} error={tried && errors.name}>
          <AInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            valid={!errors.name}
            invalid={bad("name")}
          />
        </Field>

        <Field label="Street address" error={tried && errors.street}>
          <AInput
            value={street}
            onChange={(e) => setStreet(e.target.value)}
            autoComplete="street-address"
            valid={!errors.street}
            invalid={bad("street")}
          />
        </Field>

        <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <div className="col-span-2">
            <Field label="City" error={tried && errors.city}>
              <AInput
                value={city}
                onChange={(e) => setCity(e.target.value)}
                valid={!errors.city}
                invalid={bad("city")}
              />
            </Field>
          </div>
          <Field label="State / Province" error={tried && errors.state}>
            <AInput
              value={state}
              onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
              valid={!errors.state}
              invalid={bad("state")}
            />
          </Field>
          <Field label="Postal / ZIP" error={tried && errors.zip}>
            <AInput
              value={zip}
              onChange={(e) => setZip(e.target.value.slice(0, 7))}
              valid={!errors.zip}
              invalid={bad("zip")}
            />
          </Field>
        </div>

        <Field label="Country" error={tried && errors.country}>
          <ASelect
            value={country}
            onChange={setCountry}
            options={COUNTRIES}
            placeholder="Pick a country…"
            invalid={bad("country")}
          />
        </Field>

        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="Shop phone" error={tried && errors.phone}>
            <AInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              inputMode="tel"
              autoComplete="tel"
              placeholder="(555) 123-4567"
              valid={!errors.phone}
              invalid={bad("phone")}
            />
          </Field>
          <Field label="Shop email" error={tried && errors.email}>
            <AInput
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              inputMode="email"
              autoComplete="email"
              placeholder="office@yourshop.com"
              valid={!errors.email}
              invalid={bad("email")}
            />
          </Field>
        </div>
      </div>

      <ActionRow
        back={onBack}
        note={
          tried && missing > 0
            ? `${missing} field${missing > 1 ? "s" : ""} to go — they're marked above`
            : undefined
        }
      >
        <PrimaryButton onClick={next}>
          Continue
          <ArrowRight className="size-4" />
        </PrimaryButton>
      </ActionRow>
    </div>
  )
}
